import { DOCUMENT } from '@angular/common';
import {
  Component,
  HostListener,
  inject,
  OnDestroy,
  OnInit,
  Renderer2
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import {
  ActivatedRoute,
  NavigationEnd,
  Router,
  RouterOutlet
} from '@angular/router';
import { filter, map, tap } from 'rxjs';
import { LoadingBarModule } from '@ngx-loading-bar/core';
import { LoadingBarHttpClientModule } from '@ngx-loading-bar/http-client';
import { Meta } from '@angular/platform-browser';

import { BaseComponent } from './~shared/abstract-components/base.component';
import { IconService } from './~shared/services/icon.service';
import { AuthFacade } from './~store/facades/auth.facade';
import { NgSelectConfig } from '@ng-select/ng-select';
import { NgxPermissionsService } from 'ngx-permissions';
import { flatMap, uniq } from 'lodash-es';
import { UserRole } from '~core/models/auth.models';
import { Permission } from '~core/models/roles.models';

@Component({
  selector: 'app-root',
  templateUrl: './app.component.html',
  styleUrls: ['./app.component.scss'],
  imports: [LoadingBarModule, LoadingBarHttpClientModule, RouterOutlet]
})
export class AppComponent extends BaseComponent implements OnInit, OnDestroy {
  private _iconService = inject(IconService);
  protected _authFacade = inject(AuthFacade);
  private _ngSelectConfig = inject(NgSelectConfig);
  protected _permissionsService = inject(NgxPermissionsService);
  private _renderer = inject(Renderer2);
  private _document = inject(DOCUMENT);

  @HostListener('window:online')
  onOnlineEvent(): void {
    this.setOnlineStatus(true);
  }

  @HostListener('window:offline')
  onOfflineEvent(): void {
    this.setOnlineStatus(false);
  }

  constructor(
    private _router: Router,
    private _activatedRoute: ActivatedRoute,
    private _metaService: Meta
  ) {
    super();
  }

  ngOnInit() {
    this.addMetaTags();
    if (this._isPlatformBrowser) {
      this._syncEnvironmentThemeClass();
      this.setOnlineStatus();
      this.listenToRouteParamsChanges();
    }
    this._iconService.loadIcons();
    this._initNgSelectConfig();
    this.listenToRoleChanges();
  }

  ngOnDestroy(): void {
    if (!this._isPlatformBrowser) {
      return;
    }

    this._renderer.removeClass(this._document.body, 'sandbox-theme');
  }

  listenToRouteParamsChanges(): void {
    this._router.events
      .pipe(
        takeUntilDestroyed(this._destroyRef),
        filter(event => event instanceof NavigationEnd),
        map(() => this._activatedRoute),
        map(route => {
          while (route.firstChild) {
            route = route.firstChild;
          }
          return route;
        }),
        tap(activatedRoute => {
          const { snapshot } = activatedRoute;
          const params = this._getActivatedRouteParams(activatedRoute);
          const routeData = this._globalFacade.routeData;

          this._globalFacade.setRouteData({
            params: params,
            queryParams: snapshot.queryParams,
            currentUrl: this._router.url,
            currentUrlWithoutParams: this._router.url.split('?')[0],
            previousUrl: routeData?.currentUrl || '/',
            data: snapshot.data
          });

          // this._listenToCurrentUser();
          this.setDynamicTags();
        })
      )
      .subscribe();
  }

  setOnlineStatus(status?: boolean): void {
    this._globalFacade.setOnlineStatus(
      status === undefined ? navigator.onLine : status
    );
  }

  addMetaTags(): void {
    this._metaService.updateTag({
      property: 'og:url',
      content: this.environment.baseUrl
    });
    this._metaService.updateTag({
      property: 'og:type',
      content: 'website'
    });
    this._metaService.updateTag({
      property: 'og:description',
      content: 'app'
    });
    this._metaService.updateTag({
      property: 'og:site_name',
      content: 'app'
    });
    this._metaService.updateTag({
      property: 'og:image',
      content: `${this.environment.baseUrl}/assets/icons/logo.png`
    });

    // this._metaService.updateTag({
    //   property: 'twitter:image',
    //   content: `${environment.baseUrl}/assets/icons/logo.png`
    // });
    // this._metaService.updateTag({
    //   property: 'twitter:card',
    //   content: 'summary_large_image'
    // });
    // this._metaService.updateTag({
    //   property: 'twitter:site',
    //   content: '@username'
    // });
    // this._metaService.updateTag({
    //   property: 'twitter:creator',
    //   content: '@username'
    // });
    // this._metaService.updateTag({
    //   property: 'twitter:title',
    //   content: 'app'
    // });
    // this._metaService.updateTag({
    //   property: 'twitter:description',
    //   content: 'app'
    // });
  }

  setDynamicTags(): void {
    //  TODO: set dynamic tags
  }

  listenToRoleChanges(): void {
    this._authFacade.currentUser$
      .pipe(
        takeUntilDestroyed(this._destroyRef),
        filter(Boolean),
        tap({
          next: user => {
            if (user.roles?.includes(UserRole.ROLE_SUPER_ADMIN)) {
              this._permissionsService.loadPermissions(
                Object.values(Permission)
              );
              return;
            }
            const permissions = user.scopes
              ? uniq(flatMap(user.scopes.map(scope => scope.permissions)))
              : [];
            this._permissionsService.loadPermissions(permissions);
          }
        })
      )
      .subscribe();
  }

  private _initNgSelectConfig(): void {
    this._ngSelectConfig.notFoundText = 'Aucun élément trouvé';
  }

  private _syncEnvironmentThemeClass(): void {
    this._renderer.removeClass(this._document.body, 'sandbox-theme');
    if (this.isSandboxEnvironment) {
      this._renderer.addClass(this._document.body, 'sandbox-theme');
    }
  }

  private _getActivatedRouteParams(
    route: ActivatedRoute
  ): Record<string, string> {
    const allParams = { ...route.snapshot.params };
    let currentRoute = route.parent;

    // Traverse up and accumulate params from parents
    while (currentRoute) {
      Object.assign(allParams, currentRoute.snapshot.params);
      currentRoute = currentRoute.parent;
    }

    return allParams;
  }
}
